import React, { useMemo, useRef } from "react";

import { Autocomplete, AutocompleteProps, TextField } from "@mui/material";
import { calcFieldsToDisplay } from "constants/reports";
import { useCharacteristics, useSimpleMeasures } from "hooks";
import { difference } from "lodash";
import { useCalculatedSelector } from "store/selectors/reports-selectors";
import { ReportsQueries } from "store/types/reports";

import { MeasureGroup } from "./MeasureGroup";

export type MeasuresOption = { type: string; option: string };
export type MeasuresGroupKeys = keyof Pick<ReportsQueries, "measures" | "calculatedFields" | "characteristics">;

export interface MeasuresSelectProps {
  disabled?: boolean;
  multiple?: boolean;
  disableClearable?: boolean;
  customOptions?: MeasuresOption[];
  value: MeasuresOption[];
  onChange: (value: MeasuresOption[], reason?: string) => void;
  showGroups?: MeasuresGroupKeys[];
  excludeStaticCalcFields?: boolean;
  sx?: AutocompleteProps<MeasuresOption, boolean, boolean, boolean>["sx"];
}

const groupsKeyTitle: Record<MeasuresGroupKeys, string> = {
  measures: "Measures",
  characteristics: "Characteristics",
  calculatedFields: "Calculated Measures"
};

export const MeasuresSelect: React.FC<MeasuresSelectProps> = ({
  disabled = false,
  multiple = false,
  disableClearable = false,
  customOptions,
  value,
  onChange,
  showGroups = ["measures", "calculatedFields", "characteristics"],
  excludeStaticCalcFields = false,
  sx
}) => {
  const popperRef = useRef<any>();
  const inputRef = useRef<HTMLInputElement>(null);

  const characteristicsAll = useCharacteristics();
  const calculatedFieldsAll = useCalculatedSelector();
  const simpleMeasures = useSimpleMeasures();
  const uniqueCalFieldsToShow = difference(calculatedFieldsAll, calcFieldsToDisplay);

  const options = useMemo(() => {
    if (customOptions) return customOptions;

    let options: MeasuresOption[] = [];
    if (showGroups.includes("measures")) {
      options = simpleMeasures.map((m) => ({ type: groupsKeyTitle.measures, option: String(m) }));
    }
    if (showGroups.includes("characteristics")) {
      options = [
        ...options,
        ...characteristicsAll.map((c) => ({
          type: groupsKeyTitle.characteristics,
          option: typeof c === "string" ? c : String(c.value)
        }))
      ];
    }
    if (showGroups.includes("calculatedFields")) {
      //if  excludeStaticCalcFields is true, then we need to remove from arr list of specific calculated fields (calcFieldsToDisplay),
      //because we show them in the table as static buttons in table toolbar
      excludeStaticCalcFields
        ? (options = [
            ...options,
            ...uniqueCalFieldsToShow.map((cal) => ({ type: groupsKeyTitle.calculatedFields, option: cal }))
          ])
        : (options = [
            ...options,
            ...calculatedFieldsAll.map((cal) => ({ type: groupsKeyTitle.calculatedFields, option: cal }))
          ]);
    }

    return options;
  }, [simpleMeasures, characteristicsAll, calculatedFieldsAll, uniqueCalFieldsToShow, excludeStaticCalcFields]);

  return (
    <Autocomplete<MeasuresOption, boolean, boolean>
      sx={{ minWidth: 200, ...sx }}
      disabled={disabled}
      disableCloseOnSelect={multiple}
      classes={{ listbox: "set-table-columns__listbox" }}
      limitTags={3}
      options={options}
      disableClearable={disableClearable}
      groupBy={(option) => option.type}
      multiple={multiple}
      value={multiple ? value : value[0]}
      isOptionEqualToValue={(option, value) => option.option === value.option && option.type === value.type}
      onChange={(_, value, reason) => {
        onChange(!value ? [] : Array.isArray(value) ? value : [value], reason);
      }}
      getOptionLabel={(option) => option.option}
      renderInput={(params) => <TextField inputRef={inputRef} {...params} label="Parameters" />}
      slotProps={{
        popper: {
          popperRef: popperRef
        }
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <MeasureGroup params={params} popperRef={popperRef} searchValue={inputRef.current?.value || ""} />
        </li>
      )}
    />
  );
};
